import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './PasswordResetPage.css';

const PasswordResetPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [countdown, setCountdown] = useState(3);

  // Password strength calculation
  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[^a-zA-Z\d]/.test(password)) strength++;
    return strength;
  };

  const passwordStrength = calculatePasswordStrength(password);
  const strengthLabels = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong'];
  const strengthLabel = password.length > 0 ? strengthLabels[passwordStrength] : '';

  // Debug console logs
  console.log('PasswordResetPage component mounted');
  console.log('Token from URL:', token);
  // Password reset API
  const API_URL = 'https://api.protagon.ai/api';
  console.log('API_URL:', API_URL);

  useEffect(() => {
    const verifyToken = async () => {
      setLoading(true);
      console.log('Verifying token:', token);
      try {
        // First verify the token
        console.log('Fetching token verification...');
        const url = `${API_URL}/verify-reset-token/${token}`;
        console.log('URL:', url);
        const response = await fetch(url);
        console.log('Response status:', response.status);
        const data = await response.json();
        console.log('Response data:', data);

        if (response.ok && data.email) {
          setTokenValid(true);
          setUserEmail(data.email);
          setUserName(data.user_name);
          setError('');
        } else {
          setTokenValid(false);
          setError(data.error || 'Invalid or expired reset link');
        }
      } catch (err) {
        console.error('Error verifying token:', err);
        setTokenValid(false);
        setError('An error occurred while verifying the reset link');
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      verifyToken();
    } else {
      setError('No token found in URL');
      setTokenValid(false);
      setLoading(false);
    }
  }, [token]);

  // Countdown effect for success redirect
  useEffect(() => {
    if (success && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (success && countdown === 0) {
      navigate('/cpd-pro');
    }
  }, [success, countdown, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    // Validate passwords
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setIsSubmitting(false);
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/reset-password/${token}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        // Clear form
        setPassword('');
        setConfirmPassword('');
        // Show success message for 3 seconds then redirect
        setTimeout(() => {
          window.location.href = 'https://cpd-pro.app/login';
        }, 3000);
      } else {
        setError(data.error || 'Failed to reset password');
      }
    } catch (err) {
      console.error('Error resetting password:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Header component
  const Header = () => (
    <div className="reset-header-bar">
      <div className="logo">
        CPD Pro
        <span>by Protagon</span>
      </div>
    </div>
  );

  // Loading state
  if (loading) {
    return (
      <div className="password-reset-page">
        <Header />
        <div className="reset-container">
          <div className="reset-card">
            <div className="loading">
              <div className="loading-spinner"></div>
              <p className="loading-text">Verifying reset link...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Invalid token state
  if (!tokenValid) {
    return (
      <div className="password-reset-page">
        <Header />
        <div className="reset-container">
          <div className="reset-card">
            <div className="status-container">
              <div className="status-icon error">
                ⚠
              </div>
              <h2 className="status-title error">Invalid Reset Link</h2>
              <p className="status-message">{error}</p>
              <p className="status-message">
                The reset link may have expired or been used already. Please request a new password reset link.
              </p>
              <div className="security-info">
                <h4>Security Notice</h4>
                <p>
                  For your security, password reset links expire after 1 hour. 
                  If you need to reset your password, please request a new link from the login page.
                </p>
              </div>
              <button 
                className="btn-primary"
                onClick={() => navigate('/cpd-pro')}
              >
                Back to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="password-reset-page">
        <Header />
        <div className="reset-container">
          <div className="reset-card">
            <div className="status-container">
              <div className="status-icon success">
                ✓
              </div>
              <h2 className="status-title success">Password Reset Successful!</h2>
              <p className="status-message">
                Your password has been successfully reset. You can now login with your new password.
              </p>
              <p className="status-message">
                Your account security has been updated and you're ready to continue learning.
              </p>
              <div className="countdown-container">
                <p className="countdown-text">
                  Redirecting to login page in {countdown} second{countdown !== 1 ? 's' : ''}...
                </p>
              </div>
              <button 
                className="btn-primary"
                onClick={() => navigate('/cpd-pro')}
              >
                Go to Login Now
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main password reset form
  return (
    <div className="password-reset-page">
      <Header />
      <div className="reset-container">
        <div className="reset-card">
          <div className="reset-form-header">
            <h1 className="reset-form-title">Reset Your Password</h1>
            <p className="reset-form-subtitle">
              Create a new secure password for your account
            </p>
          </div>

          {userName && (
            <div className="user-greeting">
              <p className="user-greeting-text">
                Welcome back, <span className="user-name">{userName}</span>
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="reset-form">
            {error && (
              <div className="error-message">
                {error}
              </div>
            )}
            
            <div className="form-group">
              <label htmlFor="password" className="form-label">New Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter a strong password"
                required
                minLength="8"
                className="form-input"
              />
              {password.length > 0 && (
                <div className="password-strength">
                  {[...Array(5)].map((_, i) => (
                    <div
                      key={i}
                      className={`strength-bar ${i < passwordStrength ? 'active' : ''}`}
                    />
                  ))}
                  <span className="strength-text">{strengthLabel}</span>
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword" className="form-label">Confirm New Password</label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your new password"
                required
                minLength="8"
                className="form-input"
              />
            </div>

            <div className="security-info">
              <h4>Password Requirements</h4>
              <p>
                Your password should be at least 8 characters long and include a mix of letters, 
                numbers, and symbols for maximum security.
              </p>
            </div>

            <button 
              type="submit" 
              className={`btn-submit ${isSubmitting ? 'btn-loading' : ''}`}
              disabled={isSubmitting}
            >
              {isSubmitting ? '' : 'Reset Password'}
            </button>
          </form>

          <div className="reset-footer">
            <button 
              className="link-button"
              onClick={() => navigate('/cpd-pro')}
            >
              ← Back to Login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PasswordResetPage; 